import { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileDown, IndianRupee, ShieldAlert, Boxes, Recycle } from "lucide-react";
import camApi, { STATUS_META, CONDITION_META } from "@/lib/camApi";
import { useCam } from "@/components/cam/ctx";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function Reports() {
  const { catName } = useCam();
  const [summary, setSummary] = useState(null);
  const [warranty, setWarranty] = useState([]);
  const [disposal, setDisposal] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, w, d] = await Promise.all([camApi.summary(), camApi.warranty(90), camApi.disposal()]);
        setSummary(s.summary); setWarranty(w.rows || []); setDisposal(d.rows || []);
      } catch (e) { toast.error(e.message); } finally { setLoading(false); }
    })();
  }, []);

  const exportRegister = async () => {
    try {
      const r = await camApi.assets({});
      const wb = XLSX.utils.book_new();
      const meta = [
        ["Solution — Campus Asset Register"],
        ["Generated", new Date().toLocaleString()],
        ["Total assets", (r.assets || []).length],
        [],
        ["Powered by Arventiq Labs"],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(meta), "Summary");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet((r.assets || []).map((a) => ({
        Tag: a.asset_tag, Name: a.name, Category: catName(a.category_id), Brand: a.brand || "", Model: a.model || "",
        Serial: a.serial_number || "", Status: STATUS_META[a.status]?.label || a.status, Condition: a.condition,
        Vendor: a.vendor || "", "Purchase date": a.purchase_date || "", "Cost (INR)": a.purchase_cost || "",
        "Warranty expiry": a.warranty_expiry || "",
      }))), "Assets");
      XLSX.writeFile(wb, `CAM-Asset-Register-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="glass rounded-2xl p-10 text-center text-sm text-muted-foreground">Loading reports…</div>;
  if (!summary) return <div className="glass rounded-2xl p-10 text-center text-sm text-muted-foreground">No data yet.</div>;

  const statusData = Object.entries(summary.byStatus || {}).map(([k, v]) => ({ name: STATUS_META[k]?.label || k, value: v, color: STATUS_META[k]?.color || "#64748b" }));
  const condData = Object.entries(summary.byCondition || {}).map(([k, v]) => ({ name: k, value: v, color: CONDITION_META[k]?.color || "#64748b" }));
  const catData = Object.entries(summary.byCategory || {}).map(([k, v]) => ({ name: catName(Number(k)), value: v }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="overline">Asset intelligence</div>
        <Button variant="outline" size="sm" onClick={exportRegister}><FileDown className="h-4 w-4 mr-1.5" />Export register (xlsx)</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Boxes} label="Total assets" value={summary.total} />
        <Kpi icon={IndianRupee} label="Book value" value={inr(summary.totalValue)} />
        <Kpi icon={ShieldAlert} label="Warranty <60d" value={summary.warrantySoon} tone={summary.warrantySoon ? "amber" : ""} />
        <Kpi icon={Recycle} label="Maint. overdue" value={summary.overdueMaint} tone={summary.overdueMaint ? "red" : ""} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="glass rounded-2xl p-4">
          <div className="overline mb-3">By status</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {statusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <Legend data={statusData} />
          <div className="mt-3 pt-3 border-t">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">By condition</div>
            <Legend data={condData} />
          </div>
        </div>

        <div className="glass rounded-2xl p-4">
          <div className="overline mb-3">By category</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={catData} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#0654FC" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <ReportTable title="Warranty expiring soon" empty="No warranties expiring in the next 90 days." rows={warranty}
        cols={[["asset_tag", "Tag"], ["name", "Asset"], ["warranty_expiry", "Expiry", (v) => new Date(v).toLocaleDateString()]]} catName={catName} />
      <ReportTable title="Disposed assets" empty="No disposed assets." rows={disposal}
        cols={[["asset_tag", "Tag"], ["name", "Asset"], ["purchase_cost", "Cost", (v) => v ? inr(v) : "—"], ["updated_at", "Disposed", (v) => new Date(v).toLocaleDateString()]]} catName={catName} />
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone }) {
  const toneCls = tone === "amber" ? "text-amber-600" : tone === "red" ? "text-red-500" : "";
  return (
    <div className="glass rounded-2xl p-4">
      <div className="flex items-center gap-1.5 text-muted-foreground"><Icon className="h-3.5 w-3.5" /><span className="text-[10px] uppercase tracking-widest">{label}</span></div>
      <div className={`metric text-2xl font-display mt-1 ${toneCls}`}>{value}</div>
    </div>
  );
}

function Legend({ data }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
      {data.map((d) => <span key={d.name} className="inline-flex items-center gap-1.5 text-[11px]"><span className="h-2 w-2 rounded-full" style={{ background: d.color }} />{d.name} · {d.value}</span>)}
    </div>
  );
}

function ReportTable({ title, rows, cols, empty, catName }) {
  return (
    <div className="glass rounded-2xl overflow-hidden">
      <div className="px-4 py-3 overline border-b">{title}</div>
      {rows.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">{empty}</div> : (
        <div className="overflow-x-auto touch-scroll">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b">
              {cols.map(([k, label]) => <th key={k} className="px-4 py-2">{label}</th>)}
              <th className="px-4 py-2">Category</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0">
                  {cols.map(([k, , fmt]) => <td key={k} className={`px-4 py-2 ${k === "asset_tag" ? "text-mono text-xs" : ""}`}>{fmt ? fmt(r[k]) : r[k]}</td>)}
                  <td className="px-4 py-2 text-muted-foreground">{catName(r.category_id)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
